import type { TagsEventType } from '@aytm/app/insights/dashboard/components/hooks/types'
import {
  makeBlanketDataAction,
  makeRefreshComponentDataAction
} from '@aytm/app/insights/dashboard/duckness/ducks/DataDuck/DataDuck.actionCreators'
import {
  makeUpdateEditorAction,
  makeImageUploadEditorAction
} from '@aytm/app/insights/dashboard/duckness/ducks/EditorDuck/EditorDuck.actionCreators'
import {
  makeAttributeAddByEventEventAction,
  makeAttributeDeleteEventAction,
  makeAttributeReplaceEventAction,
  makeChangeCurrentWaveEventAction,
  makeChangePresetEventAction,
  makeChartScaleChangeEventAction,
  makeChartTypeChangeEventAction,
  makeColorsChangeEventAction,
  makeElementToggleEventAction,
  makeItemPinnedEventAction,
  makeItemSelectEventAction,
  makeMetricsChangeEventAction,
  makePrimaryConfidenceLevelChangeEventAction,
  makeSecondaryConfidenceLevelChangeEventAction,
  makeSelectAttributeEventAction,
  makeShowSectionChangeEventAction,
  makeSortByAttributeEtcEventAction,
  makeSortByAttributeEventAction,
  makeSwapChangeEventAction,
  makeTrackAttributeChangeEventAction,
  makeTrackTargetChangeEventAction,
  type AttributeSortEventType
} from '@aytm/app/insights/dashboard/duckness/ducks/EventDuck/EventDuck.actionCreators'
import { DATA_ACTIONS } from '@aytm/app/insights/dashboard/duckness/InsightsDashboardPool'
import { poolDispatch } from '@aytm/app/insights/dashboard/duckness/poolDispatch'
import type { TargetType } from '@aytm/app/insights/dashboard/models/types.ClientDTO'

export const tagsUpdate = (event: TagsEventType) => {
  poolDispatch(makeBlanketDataAction(DATA_ACTIONS.TAGS_UPDATE, event))
  // InsightsDashboardPool.dispatch(DataDuck.duckName, DATA_ACTIONS.TAGS_UPDATE, event)
}

/**
 * Responsible for changes in 'editor' state variable
 */
export const editorChange = (event: unknown) => {
  poolDispatch(makeUpdateEditorAction(event))
  // InsightsDashboardPool.dispatch(EditorDuck.duckName, EDITOR_ACTIONS.UPDATE, event)
}

/**
 * Toggle visibility of certain UI element like showChart, showSigtest, etc.
 */
export const elementToggle = (event: unknown) => {
  poolDispatch(makeElementToggleEventAction(event))
  // InsightsDashboardPool.dispatch(EventDuck.duckName, EVENT_ACTIONS.ELEMENT_TOGGLE, event)
}

/**
 * Upload image
 */
export const imageUpload = (event: unknown) => {
  poolDispatch(makeImageUploadEditorAction(event))
  // InsightsDashboardPool.dispatch(EditorDuck.duckName, EDITOR_ACTIONS.IMAGE_UPLOAD, event)
}

/**
 * Replace selected subject with selected from dropdown list
 */
export const dispatchItemSelect = ({ uid }: { uid: string }) => {
  poolDispatch(makeItemSelectEventAction(uid))
  // InsightsDashboardPool.dispatch(EventDuck.duckName, EVENT_ACTIONS.ITEM_SELECT, uid)
}

/**
 * Show subject as benchmark
 */
export const itemPin = ({ uid, pinned }: { uid: string; pinned: boolean }) => {
  poolDispatch(makeItemPinnedEventAction(uid, pinned))
  // InsightsDashboardPool.dispatch(EventDuck.duckName, EVENT_ACTIONS.ITEM_PIN, { uid, pinned })
}

/**
 * Fires when user clicks on attribute
 * event.attribute field contains clicked attribute name
 */
export const sortByAttributeChange = (event: AttributeSortEventType) => {
  poolDispatch(makeSortByAttributeEventAction(event))
  // InsightsDashboardPool.dispatch(EventDuck.duckName, EVENT_ACTIONS.ATTRIBUTE_SORT, event)
}

/**
 * Changes current attribute metrics
 */
export const metricsChange = (event: unknown) => {
  poolDispatch(makeMetricsChangeEventAction(event))
  // InsightsDashboardPool.dispatch(EventDuck.duckName, EVENT_ACTIONS.ATTRIBUTE_METRICS_CHANGE, event)
}

/**
 * Fires when user deletes an attribute from current attribute list.
 * event.attribute field contains attribute name to be deleted
 */
export const attributeDelete = (event: unknown) => {
  poolDispatch(makeAttributeDeleteEventAction(event))
  // InsightsDashboardPool.dispatch(EventDuck.duckName, EVENT_ACTIONS.ATTRIBUTE_DELETE, event)
}

/**
 * Fires when deleted attribute is adding back to current list
 */
export const attributeAdd = (event: unknown) => {
  poolDispatch(makeAttributeAddByEventEventAction(event))
  // InsightsDashboardPool.dispatch(EventDuck.duckName, EVENT_ACTIONS.ATTRIBUTE_ADD, event)
}

/**
 * Fires when user drops dragged attribute on target
 * event.from field contains source attribute name
 * event.to field contains target attribute name
 */
export const attributeReplace = (event: unknown) => {
  poolDispatch(makeAttributeReplaceEventAction(event))
  // InsightsDashboardPool.dispatch(EventDuck.duckName, EVENT_ACTIONS.ATTRIBUTE_REPLACE, event)
}

/**
 * Sets current wave
 */
export const currentWaveChange = ({ wave }: { wave: string }) => {
  poolDispatch(makeChangeCurrentWaveEventAction(wave))
  // InsightsDashboardPool.dispatch(EventDuck.duckName, EVENT_ACTIONS.WAVE_CHANGE, { wave })
}

/**
 * Fires when new preset selected
 * event.preset field contains selected preset
 */
export const currentPresetChange = ({ preset, modifier }: { preset: string; modifier: unknown }) => {
  poolDispatch(makeChangePresetEventAction(preset, modifier))
  // InsightsDashboardPool.dispatch(EventDuck.duckName, EVENT_ACTIONS.PRESET_CHANGE, { preset, modifier })
}
/**
 * Shows/Hides section
 */
export const showSectionChange = ({
  section,
  target,
  show
}: {
  section: string
  target: TargetType
  show: boolean
}) => {
  poolDispatch(makeShowSectionChangeEventAction(section, target, show))
  // InsightsDashboardPool.dispatch(EventDuck.duckName, EVENT_ACTIONS.SHOW_SECTION_CHANGE, { section, target, show })
}

/**
 * Change confidence level event
 */
export const confidenceLevelChange = ({ confidenceLevel }: { confidenceLevel: string }) => {
  poolDispatch(makePrimaryConfidenceLevelChangeEventAction(confidenceLevel))
  // InsightsDashboardPool.dispatch(EventDuck.duckName, EVENT_ACTIONS.CONFIDENCE_LEVEL_CHANGE, { confidenceLevel })
}

/**
 * Change secondary confidence level event
 */
export const secondaryConfidenceLevelChange = ({ secondaryConfidenceLevel }: { secondaryConfidenceLevel: string }) => {
  poolDispatch(makeSecondaryConfidenceLevelChangeEventAction(secondaryConfidenceLevel))
  // InsightsDashboardPool.dispatch(EventDuck.duckName, EVENT_ACTIONS.SECONDARY_CONFIDENCE_LEVEL_CHANGE, {
  // secondaryConfidenceLevel
  // })
}

/**
 * Change chart scale change event
 */
export const chartScaleChange = ({ scale }: { scale: unknown }) => {
  poolDispatch(makeChartScaleChangeEventAction(scale))
  // InsightsDashboardPool.dispatch(EventDuck.duckName, EVENT_ACTIONS.CHART_SCALE_CHANGE, { scale })
}

/**
 * Change type scale change event
 */
export const chartTypeChange = ({ type }: { type: string }) => {
  poolDispatch(makeChartTypeChangeEventAction(type))
  // InsightsDashboardPool.dispatch(EventDuck.duckName, EVENT_ACTIONS.CHART_TYPE_CHANGE, { type })
}

/**
 * Change selected attributes on legend click
 */
export const chartLegendClick = ({
  section,
  attribute,
  method,
  toggle
}: {
  toggle: boolean
  section: string
  attribute: string
  method: string
}) => {
  if (toggle) {
    poolDispatch(makeSortByAttributeEtcEventAction(section, attribute, method))
    // InsightsDashboardPool.dispatch(EventDuck.duckName, EVENT_ACTIONS.ATTRIBUTE_SORT, { section, attribute, method })
  } else {
    poolDispatch(makeSelectAttributeEventAction(section, attribute, method))
    // InsightsDashboardPool.dispatch(EventDuck.duckName, EVENT_ACTIONS.LEGEND_CLICK, { section, attribute, method })
  }
}

/**
 * Change cell colors for lower/higher than norm data
 */
export const colorsChange = ({ colors }: { colors: unknown }) => {
  poolDispatch(makeColorsChangeEventAction(colors))
  // InsightsDashboardPool.dispatch(EventDuck.duckName, EVENT_ACTIONS.COLORS_CHANGE, { colors })
}

/**
 * Change current data source
 */
export const trackTargetChange = (event: unknown) => {
  poolDispatch(makeTrackTargetChangeEventAction(event))
  // InsightsDashboardPool.dispatch(EventDuck.duckName, EVENT_ACTIONS.TRACK_TARGET_CHANGE, event)
}

/**
 * Track attributes change
 */
export const trackAttributesChange = (event: unknown) => {
  poolDispatch(makeTrackAttributeChangeEventAction(event))
  // InsightsDashboardPool.dispatch(EventDuck.duckName, EVENT_ACTIONS.TRACK_ATTRIBUTES_CHANGE, event)
}

export const refreshComponent = (refreshOptions: { refresh: unknown }) => {
  poolDispatch(makeRefreshComponentDataAction(refreshOptions))
}
/**
 * Change current split
 */
export const swapChange = (swapChangeEvent: unknown) => {
  poolDispatch(makeSwapChangeEventAction(swapChangeEvent))
  // InsightsDashboardPool.dispatch(EventDuck.duckName, EVENT_ACTIONS.SWAP_CHANGE, event)
}
